import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaPhone, FaUser, FaCalendar } from 'react-icons/fa';
import { workshopAPI } from '../../services/api';

const PublicWorkshopAttendance = () => {
  const { qrCodeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [phone, setPhone] = useState('');

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phone.trim() || phone.length !== 10) {
      return;
    }

    try {
      setLoading(true);
      const response = await workshopAPI.markAttendance(qrCodeId, { phone: phone.trim() });
      setResult(response.data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error marking attendance:', error);
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to mark attendance'
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 max-w-md mx-4 text-center">
          {/* Neurons Logo */}
          <img 
            src="/neurons-logo.avif" 
            alt="Neurons School of Innovation and AI" 
            className="h-12 mx-auto mb-4"
          />
          
          {/* Result Icon */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            result?.success ? (result?.is_duplicate ? 'bg-yellow-600' : 'bg-green-600') : 'bg-red-600'
          }`}>
            {result?.success ? (
              result?.is_duplicate ? (
                <FaCheckCircle className="text-2xl text-white" />
              ) : (
                <FaCheckCircle className="text-2xl text-white" />
              )
            ) : (
              <FaTimesCircle className="text-2xl text-white" />
            )}
          </div>
          
          {/* Result Message */}
          <h1 className={`text-2xl font-bold mb-4 ${
            result?.success ? (result?.is_duplicate ? 'text-yellow-400' : 'text-green-400') : 'text-red-400'
          }`}>
            {result?.success ? (
              result?.is_duplicate ? 'Attendance Already Marked!' : 'Attendance Marked!'
            ) : (
              'Attendance Failed'
            )}
          </h1>
          
          <p className="text-gray-300 mb-6">
            {result?.message}
          </p>
          
          {result?.success && result?.student_name && (
            <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <FaUser className="text-blue-400" />
                <span className="text-sm font-medium text-gray-300">Student Details</span>
              </div>
              <div className="text-sm text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-300">Name:</span>
                  <span>{result.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-300">Phone:</span>
                  <span>{phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-300">Date:</span>
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-300">Time:</span>
                  <span>{new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className={`border border-gray-600 rounded-lg p-4 ${
            result?.success ? (result?.is_duplicate ? 'bg-yellow-900 border-yellow-700' : 'bg-blue-900 border-blue-700') : 'bg-red-900 border-red-700'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              result?.success ? (result?.is_duplicate ? 'text-yellow-200' : 'text-blue-200') : 'text-red-200'
            }`}>
              {result?.success ? (
                result?.is_duplicate ? 'What this means:' : 'What happens next?'
              ) : (
                'What to do next?'
              )}
            </h3>
            <ul className={`text-sm space-y-1 text-left ${
              result?.success ? (result?.is_duplicate ? 'text-yellow-100' : 'text-blue-100') : 'text-red-100'
            }`}>
              {result?.success ? (
                result?.is_duplicate ? (
                  <>
                    <li>• Your attendance was already recorded earlier today</li>
                    <li>• You don't need to mark attendance again</li>
                    <li>• You can participate in the workshop as usual</li>
                    <li>• Attendance can only be marked once per day</li>
                  </>
                ) : (
                  <>
                    <li>• Your attendance has been recorded</li>
                    <li>• You can now participate in the workshop</li>
                    <li>• Keep your phone number handy for future sessions</li>
                  </>
                )
              ) : (
                <>
                  <li>• Please check if you're enrolled in this workshop</li>
                  <li>• Ensure your payment has been verified</li>
                  <li>• Contact the workshop organizer if needed</li>
                </>
              )}
            </ul>
          </div>
          
          <p className="text-sm text-gray-400 mt-6">
            If you have any questions, contact us at{' '}
            <a href="mailto:info@neuronschool.in" className="text-blue-400 hover:text-blue-300 hover:underline">
              info@neuronschool.in
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 max-w-md mx-4 text-center">
        {/* Neurons Logo */}
        <img 
          src="/neurons-logo.avif" 
          alt="Neurons School of Innovation and AI" 
          className="h-12 mx-auto mb-4"
        />
        
        {/* Header */}
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCalendar className="text-2xl text-white" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-4">Mark Your Attendance</h1>
        <p className="text-gray-300 mb-6">
          Enter your registered phone number to mark your attendance for this workshop session.
        </p>
        
        {/* Attendance Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaPhone className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                placeholder="Enter your registered phone number"
                required
                maxLength="10"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use the same phone number you registered with
            </p>
          </div>
          
          <button
            type="submit"
            disabled={loading || !phone.trim() || phone.length !== 10}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
              loading || !phone.trim() || phone.length !== 10
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Marking Attendance...' : 'Mark Attendance'}
          </button>
        </form>
        
        {/* Instructions */}
        <div className="mt-6 bg-blue-900 border border-blue-700 rounded-lg p-4">
          <h3 className="font-semibold text-blue-200 mb-2">How it works:</h3>
          <ul className="text-sm text-blue-100 space-y-1 text-left">
            <li>• Enter your registered phone number</li>
            <li>• We'll verify you're enrolled in this workshop</li>
            <li>• Date and time will be automatically recorded</li>
            <li>• Your attendance will be marked instantly</li>
            <li>• You can only mark attendance once per day</li>
            <li>• If you try again, you'll see "already marked" message</li>
          </ul>
        </div>
        
        <p className="text-sm text-gray-400 mt-6">
          Need help? Contact us at{' '}
          <a href="mailto:info@neuronschool.in" className="text-blue-400 hover:text-blue-300 hover:underline">
            info@neuronschool.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default PublicWorkshopAttendance; 